import { useI18n } from 'i18n-utils'
import { ApiStatus, ContractServices, TransferCoinType } from 'network'
import { useAssetsStore, useDoubleContractAssetsAccountStore } from 'store'
import { StorageIndexDBEnum } from 'store/src/storage/StorageDB'
import { UiUtils } from '..'

export enum AssetTransferType {
  /** 钱包账户 */
  Coin = 'coin',
  /** 合约 */
  Contract = 'contract',
  /** 衍生品 */
  Derivative = 'derivative',
  /** 趣投 */
  FunGame = 'funGame',
}

export const AssetTransferNameMap = {
  [AssetTransferType.Coin]: '钱包账户',
  [AssetTransferType.Contract]: '合约账户',
  [AssetTransferType.Derivative]: '衍生品账户',
  [AssetTransferType.FunGame]: '趣投账户',
}

type AssetTransferFormData = {
  itemId: ItemId | undefined
  amount: string
}

export const useAssetTransfer = () => {
  const assetTransferVisible = ref(false)
  const fromAssetType = ref<AssetTransferType>(AssetTransferType.Coin)
  const toAssetType = ref<AssetTransferType>(AssetTransferType.Coin)
  const assetStore = useAssetsStore()
  const doubleContractAssetsAccountStore = useDoubleContractAssetsAccountStore()
  const loading = ref(false)
  const { t } = useI18n()
  const itemContractId = 10007
  const itemDerivativeInId = 10007
  const itemDerivativeOutId = 100007

  watch(
    () => assetTransferVisible.value,
    (visible) => {
      if (visible) {
        assetStore.refresh()
        doubleContractAssetsAccountStore.refresh()
      }
    },
  )
  const formData = ref<AssetTransferFormData>({
    itemId: 10007,
    amount: '',
  })
  watch(
    () => [fromAssetType.value, toAssetType.value],
    () => {
      formData.value.itemId =
        fromAssetType.value === AssetTransferType.Derivative ? itemDerivativeOutId : toAssetType.value === AssetTransferType.Derivative ? itemDerivativeInId : itemContractId
    },
    {
      immediate: true,
    },
  )

  const open = () => {
    assetTransferVisible.value = true
  }

  const close = () => {
    assetTransferVisible.value = false
    doubleContractAssetsAccountStore.stopFetchData()
  }

  const setFromAssetType = (type: AssetTransferType) => {
    fromAssetType.value = type
  }

  const setToAssetType = (type: AssetTransferType) => {
    toAssetType.value = type
  }

  const transformFromAndToAssetType = () => {
    const temp = fromAssetType.value
    fromAssetType.value = toAssetType.value
    toAssetType.value = temp
    formData.value.itemId =
      fromAssetType.value === AssetTransferType.Derivative ? itemDerivativeOutId : toAssetType.value === AssetTransferType.Derivative ? itemDerivativeInId : itemContractId
  }

  const itemName = computed(() => {
    const coin = assetStore.assetItemList.find((item) => item.itemId === formData.value.itemId)
    return coin?.itemName?.toUpperCase()
  })

  const itemIcon = computed(() => {
    const coin = assetStore.assetItemList.find((item) => item.itemId === formData.value.itemId)
    return coin?.itemDetail.coinUrl
  })

  const maxTransferAmount = computed(() => {
    if (fromAssetType.value === AssetTransferType.Contract) {
      return doubleContractAssetsAccountStore.doubleContractAssetsAccount?.maxWithdrawAmount || '0'
    }
    if (fromAssetType.value === AssetTransferType.Coin) {
      const coin = assetStore.assets?.remain.list.find((item) => item.itemId === formData.value.itemId)
      return coin?.over || '0'
    }
    return '0'
  })

  const openContract = async () => {
    if (doubleContractAssetsAccountStore.openContractFeature === '1') return
    UiUtils.showModal(t('合约交易属于高风险交易行为，在带来更多潜在收益的同时，也潜在巨大风险；请理性对待每一次交易行为，并且在市场出现剧烈波动时，您的余额有可能全部亏损'), {
      title: t('温馨提示'),
      confirmButtonText: t('我知道了'),
      onConfirm: async () => {
        const res = await ContractServices.openContract()
        if (res.status === ApiStatus.Success) {
          doubleContractAssetsAccountStore.openContractFeature = '1'
          window.localStorage.setItem(StorageIndexDBEnum.ExchangeOpenContractFeature, '1')
        }
      },
    })
  }

  const onTransfer = async () => {
    if (doubleContractAssetsAccountStore.openContractFeature !== '1' && toAssetType.value === AssetTransferType.Contract) {
      openContract()
      return
    }
    if (!formData.value.amount || formData.value.amount.isEqualTo(0)) {
      UiUtils.showError(t('请输入划转数量'))
      return
    }

    if (formData.value.amount.isGreaterThan(maxTransferAmount.value)) {
      UiUtils.showError(t('可用资产不足'))
      return
    }
    loading.value = true
    const nonce = Math.floor(Math.random() * (999999 - 100000 + 1)) + 100000
    const batchNo = `${Date.now()}_${nonce}`
    // 直接转到普通账户
    if (toAssetType.value === AssetTransferType.Coin || fromAssetType.value === AssetTransferType.Coin) {
      if (toAssetType.value === AssetTransferType.Contract || fromAssetType.value === AssetTransferType.Contract) {
        const res = await ContractServices.transferCoin({
          itemId: formData.value.itemId!,
          amount: formData.value.amount,
          type: fromAssetType.value === AssetTransferType.Coin ? TransferCoinType.Contract : TransferCoinType.Coin,
          batchNo,
        }).finally(() => {
          loading.value = false
        })
        if (res.status !== ApiStatus.Success) {
          UiUtils.showError(res.msg)
          loading.value = false
          return
        }
      }
    } else {
      // 需要普通账户中转
      if (fromAssetType.value === AssetTransferType.Contract) {
        const res = await ContractServices.transferCoin({
          itemId: itemContractId,
          amount: formData.value.amount,
          type: TransferCoinType.Coin,
          batchNo,
        }).finally(() => {
          loading.value = false
        })
        if (res.status !== ApiStatus.Success) {
          UiUtils.showError(res.msg)
          loading.value = false
          return
        }
      }
      // 从余额划走到目标账户
      if (toAssetType.value === AssetTransferType.Contract) {
        const res = await ContractServices.transferCoin({
          itemId: itemContractId,
          amount: formData.value.amount,
          type: TransferCoinType.Contract,
          batchNo,
        }).finally(() => {
          loading.value = false
        })
        if (res.status !== ApiStatus.Success) {
          UiUtils.showError(res.msg)
          loading.value = false
          return
        }
      }
    }
    UiUtils.showSuccess(t('划转成功'))
    loading.value = false
    assetStore.refresh()
    close()
  }

  return {
    assetTransferVisible,
    open,
    close,
    setFromAssetType,
    transformFromAndToAssetType,
    fromAssetType,
    toAssetType,
    setToAssetType,
    formData,
    itemName,
    itemIcon,
    maxTransferAmount,
    onTransfer,
    loading,
  }
}
